/**
 * PMCompletedProjects - Tab 5: Completed Projects
 *
 * Lifecycle filter: pm_readiness IN ('READY_FOR_BILLING', 'CLOSED')
 *
 * Historical record of finished projects. Read-only archive with:
 * - Project summary
 * - Completion date
 * - Option to reopen (for edge cases)
 */

import { useState, useEffect, useCallback } from "react";

export default function PMCompletedProjects({ acquireAccessToken }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  // Fetch completed projects
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const token = await acquireAccessToken();
      if (!token) {
        setError("Authentication required");
        return;
      }

      // Fetch both READY_FOR_BILLING and CLOSED
      const params = new URLSearchParams();
      params.set("pmReadiness", "READY_FOR_BILLING,CLOSED");
      const url = `/api/pm-center/projects/my-projects?${params}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch projects");
      }

      const data = await res.json();
      setProjects(data.projects || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching completed projects:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [acquireAccessToken]);

  // Load projects on mount
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Handle reopening a project (edge case - moves back to WO_PUBLISHED)
  const handleReopen = useCallback(async (project) => {
    const confirmed = confirm(
      `Reopen this project?\n\n` +
      `Project: ${project.quote_number} - ${project.customer_name}\n\n` +
      `This will move the project back to Active Projects (Tab 4).`
    );
    if (!confirmed) return;

    try {
      const token = await acquireAccessToken();
      if (!token) return;

      const res = await fetch(
        `/api/pm-center/projects/${encodeURIComponent(project.quote_number)}/reopen`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Failed to reopen project");
      }

      alert("Project reopened! It will now appear in Active Projects.");
      setSelectedProject(null);
      fetchProjects();
    } catch (err) {
      console.error("Error reopening project:", err);
      alert(`Error: ${err.message}`);
    }
  }, [acquireAccessToken, fetchProjects]);

  // Handle closing a project (move from READY_FOR_BILLING to CLOSED)
  const handleClose = useCallback(async (project) => {
    if (project.pm_readiness === 'CLOSED') {
      alert("Project is already closed.");
      return;
    }

    const confirmed = confirm(
      `Close this project?\n\n` +
      `Project: ${project.quote_number} - ${project.customer_name}\n\n` +
      `This marks the project as fully closed out.`
    );
    if (!confirmed) return;

    try {
      const token = await acquireAccessToken();
      if (!token) return;

      const res = await fetch(
        `/api/pm-center/projects/${encodeURIComponent(project.quote_number)}/close`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Failed to close project");
      }

      alert("Project closed!");
      fetchProjects();
    } catch (err) {
      console.error("Error closing project:", err);
      alert(`Error: ${err.message}`);
    }
  }, [acquireAccessToken, fetchProjects]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "No date";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Get status badge
  const getStatusBadge = (pmReadiness) => {
    if (pmReadiness === 'CLOSED') {
      return { bg: "bg-gray-100", text: "text-gray-700", label: "Closed" };
    }
    return { bg: "bg-purple-100", text: "text-purple-700", label: "Ready for Billing" };
  };

  // Empty state
  if (!loading && projects.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <div className="text-6xl mb-4">✓</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Completed Projects</h2>
          <p className="text-gray-600 max-w-md">
            Completed projects will appear here after they're marked complete in Active Projects.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Project List (left panel) */}
      <div className="w-1/3 min-w-[300px] max-w-[400px] border-r border-gray-200 overflow-y-auto bg-white">
        <div className="p-3 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-700">
              Completed ({projects.length})
            </h3>
            <button
              onClick={fetchProjects}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-center text-gray-500">Loading projects...</div>
        ) : error ? (
          <div className="p-4 text-center text-red-500">{error}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {projects.map((project) => {
              const status = getStatusBadge(project.pm_readiness);
              const isSelected = selectedProject?.quote_number === project.quote_number;

              return (
                <div
                  key={project.quote_number}
                  onClick={() => setSelectedProject(project)}
                  className={`p-3 cursor-pointer transition-colors ${
                    isSelected
                      ? "bg-purple-50 border-l-4 border-purple-500"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {project.quote_number}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${status.bg} ${status.text}`}>
                          {status.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {project.customer_name}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {project.description || "No description"}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Panel (right panel) */}
      <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
        {selectedProject ? (
          <div className="flex-1 overflow-auto p-6">
            <div className="max-w-2xl mx-auto">
              {/* Project Header */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {selectedProject.quote_number}
                    </h2>
                    <p className="text-gray-600 mt-1">
                      {selectedProject.customer_name}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      {selectedProject.description || "No description"}
                    </p>
                  </div>
                  <div>
                    {(() => {
                      const status = getStatusBadge(selectedProject.pm_readiness);
                      return (
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.bg} ${status.text}`}>
                          {status.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {/* Project Details */}
                <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Ship Date</p>
                    <p className="font-medium">{formatDate(selectedProject.ship_date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Assigned At</p>
                    <p className="font-medium">{formatDate(selectedProject.assigned_at)}</p>
                  </div>
                  {selectedProject.jobboss_order_number && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Order Number</p>
                      <p className="font-medium">{selectedProject.jobboss_order_number}</p>
                    </div>
                  )}
                  {selectedProject.wo_number && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Work Order</p>
                      <p className="font-medium">{selectedProject.wo_number}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="font-medium text-gray-700 mb-3">Actions</h3>
                <div className="flex gap-2">
                  {selectedProject.pm_readiness === 'READY_FOR_BILLING' && (
                    <button
                      onClick={() => handleClose(selectedProject)}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      Close Project
                    </button>
                  )}
                  <button
                    onClick={() => handleReopen(selectedProject)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Reopen Project
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Reopening will move the project back to Active Projects.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p>Select a project to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
